import React from 'react';
import { Button } from 'primereact/button';
import { Chip } from 'primereact/chip';
import './NotificationFilterBarMolecule.scss';

export interface NotificationTypeConfig {
  value: string;
  label: string;
  icon: string;
  color?: string;
}

export interface NotificationFilterBarMoleculeProps {
  notificationTypes: NotificationTypeConfig[];
  selectedTypes: string[];
  onFilterChange: (selectedTypes: string[]) => void;
  showUnreadOnly: boolean;
  onToggleUnreadOnly: () => void;
  unreadCount?: number;
  className?: string;
}

/**
 * NotificationFilterBarMolecule - Filter controls for notification types
 * Based on NotificationBell's filter-buttons (lines 109-169)
 */
const NotificationFilterBarMolecule: React.FC<NotificationFilterBarMoleculeProps> = ({
  notificationTypes,
  selectedTypes,
  onFilterChange,
  showUnreadOnly,
  onToggleUnreadOnly,
  unreadCount = 0,
  className = '',
}) => {
  const handleFilterClick = (typeValue: string | null) => {
    if (typeValue === null) {
      // "All" button clicked
      onFilterChange([]);
      return;
    }

    // Toggle specific type
    if (selectedTypes.includes(typeValue)) {
      onFilterChange(selectedTypes.filter((t) => t !== typeValue));
    } else {
      onFilterChange([...selectedTypes, typeValue]);
    }
  };

  const handleClearFilters = () => {
    onFilterChange([]);
    if (showUnreadOnly) {
      onToggleUnreadOnly();
    }
  };

  const hasActiveFilters = selectedTypes.length > 0 || showUnreadOnly;
  const showAllSelected = selectedTypes.length === 0;

  return (
    <div className={`notification-filter-bar ${className}`}>
      <div className="notification-filter-label">Filter by:</div>

      <div className="notification-filter-buttons">
        <Button
          label="All"
          icon="pi pi-list"
          text
          size="small"
          className={`notification-filter-btn ${showAllSelected && !showUnreadOnly ? 'active' : ''}`}
          onClick={() => handleFilterClick(null)}
        />

        {notificationTypes.map((type) => (
          <Button
            key={type.value}
            label={type.label}
            icon={`pi ${type.icon}`}
            text
            size="small"
            className={`notification-filter-btn ${selectedTypes.includes(type.value) ? 'active' : ''}`}
            onClick={() => handleFilterClick(type.value)}
          />
        ))}

        <Button
          label={unreadCount > 0 ? `Unread (${unreadCount})` : 'Unread'}
          icon="pi pi-circle-fill"
          text
          size="small"
          className={`notification-filter-btn notification-filter-unread ${showUnreadOnly ? 'active' : ''}`}
          onClick={onToggleUnreadOnly}
        />

        {hasActiveFilters && (
          <Button label="Clear" icon="pi pi-times" text size="small" severity="secondary" className="notification-filter-clear" onClick={handleClearFilters} />
        )}
      </div>

      {hasActiveFilters && (
        <div className="notification-active-filters">
          {selectedTypes.map((typeValue) => {
            const type = notificationTypes.find((t) => t.value === typeValue);
            if (!type) return null;
            return (
              <Chip
                key={typeValue}
                label={type.label}
                icon={type.icon === 'perspective-icon' ? type.icon : `pi ${type.icon}`}
                removable
                onRemove={() => {
                  handleFilterClick(typeValue);
                  return true;
                }}
                className="notification-active-filter-chip"
              />
            );
          })}
          {showUnreadOnly && (
            <Chip
              label="Unread only"
              icon="pi pi-circle-fill"
              removable
              onRemove={() => {
                onToggleUnreadOnly();
                return true;
              }}
              className="notification-active-filter-chip"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationFilterBarMolecule;
